import React, { useState, useMemo, useEffect, useContext } from 'react';
import {
  IonGrid, IonRow, IonCol, IonReorder, IonReorderGroup, IonIcon,
  IonCard, IonCardHeader, IonCardTitle, IonCardContent,
  IonButtons, IonToolbar, IonButton,
  ItemReorderEventDetail,
  IonItem,
  IonLabel,
  IonAvatar
} from '@ionic/react';
import { add, addCircle, addCircleOutline, albumsOutline, ellipsisHorizontal, listOutline } from 'ionicons/icons';
import { getStorage, ref, getDownloadURL } from "firebase/storage";
import '../theme/lists.css';
import GlobalContext from '../domain/GlobalContext';
import Image from './Image';

type ColumnDefinition = {
  attribute?: string;
  icon?: string;
  dynamicIcon?: (item: any) => string;
  onClick?: (item: any) => void;
  filePath?: string;
  label: string;
  size?: string | "auto";
  primary?: boolean;
  isImage?: boolean;
  avatar?: string;
  tags?: string[];
};

type ListViewProps<T> = {
  items: T[] | null | undefined;
  columns?: ColumnDefinition[];
  artifact_parameters?: any;
  enableReorder: boolean;
  editMode?: 'owner' | 'admin' | 'all';
  defaultLayout: 'table' | 'cards';
  showHeader?: boolean;
  showActions?: boolean;
  buttonLabel?: string | null;
  showHeaderActions?: boolean;
  showCreate?: boolean;
  addMore?: boolean;
  addMoreLabel?: string;
  onEdit?: (item: T | null) => void;
  onClick?: (item: T) => void;
  onReorder?: (event: CustomEvent) => void | undefined;
  colSize?: number[] | [12, 6, 4, 3 ] | null
  tags?: string[];
};

const ListView = <T extends { [key: string]: any }>({
  items, columns, artifact_parameters, enableReorder, editMode, defaultLayout, showActions, buttonLabel = null, showHeaderActions = true, showCreate, addMore, addMoreLabel, showHeader, onEdit, onReorder, onClick, colSize
}: ListViewProps<T>) => {

  const { user } = useContext(GlobalContext);

  if (!colSize)
    colSize = [12, 6, 4, 3];

  if (!editMode || editMode == null)
    editMode = "all";

  if (!items || items == null)
    items = [];

  if (!columns && artifact_parameters) {

    columns = generateColumnDefinitions(artifact_parameters);
  }

  if (!columns || columns == null)
    columns = [];
  
  const [layout, setLayout] = useState<'table' | 'cards'>(defaultLayout);
  const primaryColumn = columns.find(column => column.primary);

  type SizeKey = 'small' | 'medium' | 'large' | 'auto';

  const renderTags = (tags: string[], item: T) => {
    if (!tags) return null;
    return tags.map((tagDef, index) => {
      const tagContent = tagDef.replace(/\{([^}]+)\}/g, (_, key) => item[key] || '');
      return <span className='tag'>{tagContent}</span>;
    });
  };

  // Function to calculate size mapping
  const calculateSizeMapping = () => {
    const sizeValues: { [key in SizeKey]: number } = { small: 1, medium: 2, large: 3, auto: 1 };
    let totalSizeValue = columns?.reduce((total, col) => {
      // Ensure the size key is a valid key of sizeValues
      const sizeKey: SizeKey = col.size as SizeKey || 'auto';
      return total + sizeValues[sizeKey];
    }, 0);

    return columns?.map(col => {
      const sizeKey: SizeKey = col.size as SizeKey || 'auto';
      if (!totalSizeValue)
        totalSizeValue = 1;

      let maxCols = 12;
      if (enableReorder)
        maxCols = maxCols - 1;
      if (onEdit)
        maxCols = maxCols - 1;

      return {
        ...col,
        calculatedSize: `${Math.round((sizeValues[sizeKey]) / totalSizeValue * maxCols)}`
      };
    });
  };

  const getDynamicIcon = (item: T, column: ColumnDefinition) => {
    if (column.dynamicIcon) {
      const iconName = column.dynamicIcon(item);
      return iconName;
    }
    return column.icon;
  };  

  // ARTIFACTS

  type ArtifactProperties = {
    [key: string]: {
      type: string;
      description: string;
    };
  };

  type ArtifactParameters = {
    type: string;
    properties: ArtifactProperties;
    required: string[];
  };
  
  function generateColumnDefinitions(artifactParameters: any): ColumnDefinition[] {
    // Directly log the received artifactParameters for debugging
  
    try {
      if (typeof artifactParameters === 'string') {
        artifactParameters = JSON.parse(artifactParameters);
      }
    } catch (error) {}
  
    // Validate artifactParameters structure
    if (!artifactParameters || typeof artifactParameters !== 'object' || !artifactParameters.properties) {
      console.error('Invalid artifactParameters 2:', artifactParameters);
      return [];
    }
  
    const { properties, required = [] } = artifactParameters;
  
    // Generate column definitions, filtering out 'artifact_uid'
    return Object.keys(properties)
      .filter((key) => key !== 'artifact_uid') // Filter out 'artifact_uid'
      .map((key) => ({
        attribute: key,
        label: properties[key].description || key,
        primary: required.includes(key),
      }));
  }

  function getAttributeValue<T extends { [key: string]: any, content_json?: any }>(item: T, attribute: string | undefined): any {

    if (!attribute) return "";
    if (item.content_json && item.content_json[attribute] !== undefined) {
      return item.content_json[attribute];
    }
    return item[attribute];
  }  

  // Memoize the size mapping
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const sizeMapping = useMemo(() => calculateSizeMapping(), [columns]);

  const renderRows = () => {
    return (
      <IonReorderGroup disabled={!enableReorder} onIonItemReorder={onReorder}>
        {items?.map((item, index) => (
          <IonRow key={item.uid} className="list-row ion-no-padding" style={{ flexWrap: "nowrap", alignItems: "flex-start", display: "flex" }}>
            {enableReorder && (
              <IonCol size="auto" style={{ flex: '0 0 auto' }}>
                <IonReorder />
              </IonCol>
            )}
            {sizeMapping?.map((column) => (
              <IonCol
                key={`${item.id}-${column.attribute || column.label}`}
                size={column.calculatedSize}
                onClick={column.onClick ? () => column.onClick!(item) : (onClick ? () => onClick(item) : undefined)}
                style={{ flex: '0 1 auto' }}
              >
                {column.attribute && (
                  <>
                    {column.attribute === "image_prompt" ? (
                      <div className={((onClick && column.primary) || column.onClick) ? 'list-content link link-anchor' : 'list-content'}
                      style={{ width: "90%", whiteSpace: 'normal', wordWrap: 'break-word', border: "0px" }} // Allows text to wrap within the column
                        >
                        <Image
                          src={(item.content_json && item.content_json.image_url != undefined && item.content_json.image_url != null) ? item.content_json.image_url : item.image_url}
                          width="100%"
                          maxWidth="150px"
                          onClick={undefined}
                          isLoading={false}
                          setIsLoading={undefined}
                        />
                      </div>
                    ) :
                    column.isImage ? (
                      <div className={((onClick && column.primary) || column.onClick) ? 'list-content link link-anchor' : 'list-content'}
                      style={{ width: "90%", whiteSpace: 'normal', wordWrap: 'break-word', border: "0px" }}
                        >
                        <Image
                          src={getAttributeValue(item, column.attribute)}
                          width="100%"
                          maxWidth="100px"
                          onClick={undefined}
                          isLoading={false}
                          setIsLoading={undefined}
                        />
                      </div>
                    ) :
                    column.avatar != null ? (
                      <div className={((onClick && column.primary) || column.onClick) ? 'list-content link link-anchor' : 'list-content'}
                      style={{ width: "70%", whiteSpace: 'normal', wordWrap: 'break-word', border: "0px" }}
                        >
                        <IonItem color="none" lines="none">
                          <IonAvatar className="chat-avatar">
                            <Image src={column.avatar} avatar={true} />
                          </IonAvatar>
                          <IonLabel>
                          {getAttributeValue(item, column.attribute) ? (
                              getAttributeValue(item, column.attribute).length > 100
                                ? `${getAttributeValue(item, column.attribute).slice(0, 200)}...`
                                : getAttributeValue(item, column.attribute)
                            ) : ''}
                          </IonLabel>
                        </IonItem>
                      </div>
                    )
                    : (
                      <div className={((onClick && column.primary) || column.onClick) ? 'list-content link link-anchor' : 'list-content'}
                      style={{ whiteSpace: 'normal', wordWrap: 'break-word' }} // Allows text to wrap within the column
                        >
                        {getAttributeValue(item, column.attribute) ? (
                          getAttributeValue(item, column.attribute).length > 100
                            ? `${getAttributeValue(item, column.attribute).slice(0, 200)}...`
                            : getAttributeValue(item, column.attribute)
                        ) : ''}
                      </div>
                    )}
                  </>
                )}
                {column.tags && (
                  <div className={(onClick && column.primary) ? 'list-tags link link-anchor' : 'list-tags'}>
                    {renderTags(column.tags, item)}
                  </div>
                )}
                {column.icon && (
                  <div className={((onClick && column.primary) || column.onClick) ? 'list-content link link-anchor' : 'list-content'}>
                    <IonIcon icon={column.icon}></IonIcon>
                  </div>
                )}
                {column.dynamicIcon && (
                  <div className={((onClick && column.primary) || column.onClick) ? 'list-content link link-anchor' : 'list-content'}>
                    <IonIcon icon={getDynamicIcon(item, column)}></IonIcon>
                  </div>
                )}
              </IonCol>
            ))}
            {(showActions || (editMode === "owner" && user?.auth_id === item.created_by)) ? (
              <IonIcon
                icon={ellipsisHorizontal}
                onClick={onEdit ? () => onEdit(item) : undefined}
                className="list-edit-table"
              />
            ) : null}
          </IonRow>
        ))}
      </IonReorderGroup>

    );
  };

  const renderCard = (item: T, index: number) => {
    return (
      <IonCol key={item.id} size-xs={colSize ? colSize[0] : 12} size-sm={colSize ? colSize[1] : 6} size-md={colSize ? colSize[2] : 4} size-lg={colSize ? colSize[3] : 2}>
        <IonCard className="list">
          <IonCardHeader className="list">            
              {primaryColumn && primaryColumn.avatar ? (

                  <div style={{
                    textAlign: 'left',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    width: '110%',
                    position: 'relative',
                    left: '-16px'
                  }}>
                  <IonItem color="none" lines="none" className="list-avatar">
                    <IonAvatar className="list-avatar">
                      <Image src={getAttributeValue(item, primaryColumn.avatar)} avatar={true} />            
                    </IonAvatar>
                    <IonLabel className="list-avatar">
                      <div className="link list">
                        {primaryColumn.attribute
                        ? getAttributeValue(item, primaryColumn.attribute)
                        : item.name}
                      </div>
                    </IonLabel>
                  </IonItem>
                  </div>
              ) : (
                <IonCardTitle onClick={onClick ? () => onClick(item) : undefined} className={onClick ? "link list" : "list"}>
                <div style={{
                    display: '-webkit-box',
                    minHeight: '34px',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    textAlign: 'left',
                  }}
                >
                {primaryColumn && primaryColumn.attribute
                  ? getAttributeValue(item, primaryColumn.attribute)
                  : item.name}
                  </div>
                </IonCardTitle>
              )}
          </IonCardHeader>
          <IonCardContent className="list">
            <div>
              {sizeMapping?.map((column) => (
                !column.primary || column.isImage || column.attribute === "image_prompt" ? (
                  <div className="list-attribute" key={`${item.id}-${column.attribute || column.label}`}>
                    {column.attribute === "image_prompt" ? (
                        <Image
                          src={(item.content_json && item.content_json.image_url != undefined && item.content_json.image_url != null) ? item.content_json.image_url : item.image_url}
                          width="100%"
                          maxWidth="200px"
                          isLoading={false}
                          onClick={onEdit ? () => onEdit(item) : undefined}
                          setIsLoading={undefined}
                        />
                    ) :
                    column.isImage ? (
                      <Image
                        src={getAttributeValue(item, column.attribute)}
                        width="100%"
                        maxWidth="250px"
                        isLoading={false}
                        onClick={onEdit ? () => onEdit(item) : undefined}
                        setIsLoading={undefined}
                      />
                    ) :
                    column.avatar ? (
                      <IonItem color="none" lines="none">
                        <IonAvatar className="chat-avatar">
                          <Image src={column.avatar} avatar={true} />            
                        </IonAvatar>
                        <IonLabel>
                        {getAttributeValue(item, column.attribute) ? (
                            getAttributeValue(item, column.attribute).length > 100
                              ? `${getAttributeValue(item, column.attribute).slice(0, 200)}...`
                              : getAttributeValue(item, column.attribute)
                          ) : ''}
                        </IonLabel>
                      </IonItem>
                    )
                    : ( 
                      <div style={{
                          display: '-webkit-box',
                          minHeight: '30px',
                          WebkitLineClamp: 6,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}>
                        {column.attribute && <span className="list-attribute-label">{column.label}: </span>}
                        {column.attribute && <span>{getAttributeValue(item, column.attribute)}</span>}
                        {column.tags && <span>{renderTags(column.tags, item)}</span>}
                      </div>
                    )}
                  </div>
                ) : null 
              ))}            
            {buttonLabel && <IonButton onClick={onClick ? () => onClick(item) : undefined} style={{
                                  width: "100%",
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  display: "inline-block",
                                }}>{getAttributeValue(item, buttonLabel) != null ? getAttributeValue(item, buttonLabel) : "Create new"}</IonButton>}          
            </div>
          </IonCardContent>
          {showActions && onEdit ? 
            <IonIcon icon={ellipsisHorizontal} onClick={() => onEdit(item)} className='link list-edit-card' />
          :<></> }
        </IonCard>
      </IonCol>
    );
  };

  if (!items) {

    return (<></>);
  }
  else {

    return (
      <div className="list-container">      
        {((showActions == null || showActions != false || showCreate == true) && showHeaderActions) ? 
            (<IonToolbar className='list'>
                <IonButtons slot="start" className="list">
                  <IonButton color="primary" onClick={onEdit ? () => onEdit(null) : undefined}>
                    <IonIcon icon={addCircle} size='small' />
                  </IonButton>
                </IonButtons>
                <IonButtons slot="end" className="list">
                  <IonButton onClick={() => setLayout('table')} color={layout === 'table' ? "primary" : undefined}>
                    <IonIcon icon={listOutline} size='small' />
                  </IonButton>
                  <IonButton onClick={() => setLayout('cards')} color={layout === 'cards' ? "primary" : undefined}>
                    <IonIcon icon={albumsOutline} size='small' />
                  </IonButton>
                </IonButtons>
              </IonToolbar>)
              :
              (<></>)}        
        <IonGrid>
          {layout === 'table' ? (
            <>
              {(showHeader == null || showHeader != false) ? 
              (<><IonRow>
                {enableReorder && <IonCol size="auto"><div style={{ width: "30px" }} >&nbsp;</div></IonCol>}
                {sizeMapping?.map(column => (
                  <IonCol key={`header-${column.attribute}`} size={column.calculatedSize}><div className='list-header'>{column.label}</div></IonCol>
                ))}
                {showActions ? 
                <IonCol size="auto"><div style={{ width: "30px" }} >&nbsp;</div></IonCol>
                :<></> }
              </IonRow></>) 
              :
              (<></>)}
              {renderRows()}
            </>
          ) : (
            <IonRow>
              {items?.map((item, index) => renderCard(item, index))}

              {addMore && (
                <IonCol size-xs={colSize ? colSize[0] : 12} size-sm={colSize ? colSize[1] : 6} size-md={colSize ? colSize[2] : 4} size-lg={colSize ? colSize[3] : 2}>
                  <IonCard className="list">
                    <IonCardHeader className="list">
                      <div style={{ textAlign: "center", margin: "10px"}} onClick={onEdit ? () => onEdit(null) : undefined} className='link list-edit-card'>
                        <IonItem color="none" lines="none"><IonIcon icon={addCircleOutline} className="menu" color="medium" /><span className="menu-sub">{addMoreLabel ? addMoreLabel : "Create new" }</span></IonItem>
                      </div>
                    </IonCardHeader>
                  </IonCard>
                </IonCol>
              )}

            </IonRow>
          )}
        </IonGrid>
      </div>
    );
  }
};

export default ListView;